/**
 * CardSuits - Canonical suit and trump suit definitions, metadata, and normalization helpers.
 * Serves as the single source of truth for suit metadata, aliases, wire representation,
 * colors, and symbols across AtlCardSharks.
 */

export const CardSuit = {
  Spades: 'Spades',
  Hearts: 'Hearts',
  Clubs: 'Clubs',
  Diamonds: 'Diamonds',
  Joker: 'Joker',
} as const;

export type CardSuit = typeof CardSuit[keyof typeof CardSuit];

export const TrumpSuitEnum = {
  Spades: 'Spades',
  Hearts: 'Hearts',
  Clubs: 'Clubs',
  Diamonds: 'Diamonds',
  NoTrump: 'NoTrump',
} as const;

export type TrumpSuitEnum = typeof TrumpSuitEnum[keyof typeof TrumpSuitEnum];

/**
 * Union of all canonical suit representations (physical cards and trump contracts).
 */
export type CanonicalSuit = CardSuit | TrumpSuitEnum;

export type SuitColor = 'red' | 'black' | 'gold';

export interface SuitMetadata {
  readonly canonical: CanonicalSuit;
  readonly shortCode: string;
  readonly aliases: readonly string[];
  readonly symbol: string;
  readonly color: SuitColor;
  readonly isStandard: boolean;
  readonly isPhysical: boolean;
  readonly isTrump: boolean;
}

/**
 * Authoritative single source of truth for all suit metadata, symbols, colors, and aliases.
 */
export const SUIT_METADATA = {
  [CardSuit.Spades]: {
    canonical: CardSuit.Spades,
    shortCode: 'S',
    aliases: ['S', 'SPADES', 'SPADE'],
    symbol: '♠',
    color: 'black',
    isStandard: true,
    isPhysical: true,
    isTrump: true,
  },
  [CardSuit.Hearts]: {
    canonical: CardSuit.Hearts,
    shortCode: 'H',
    aliases: ['H', 'HEARTS', 'HEART'],
    symbol: '♥',
    color: 'red',
    isStandard: true,
    isPhysical: true,
    isTrump: true,
  },
  [CardSuit.Diamonds]: {
    canonical: CardSuit.Diamonds,
    shortCode: 'D',
    aliases: ['D', 'DIAMONDS', 'DIAMOND'],
    symbol: '♦',
    color: 'red',
    isStandard: true,
    isPhysical: true,
    isTrump: true,
  },
  [CardSuit.Clubs]: {
    canonical: CardSuit.Clubs,
    shortCode: 'C',
    aliases: ['C', 'CLUBS', 'CLUB'],
    symbol: '♣',
    color: 'black',
    isStandard: true,
    isPhysical: true,
    isTrump: true,
  },
  [CardSuit.Joker]: {
    canonical: CardSuit.Joker,
    shortCode: 'JOKER',
    aliases: ['JOKER', 'JOKERS', 'BJ', 'LJ'],
    symbol: '★',
    color: 'gold',
    isStandard: false,
    isPhysical: true,
    isTrump: false,
  },
  [TrumpSuitEnum.NoTrump]: {
    canonical: TrumpSuitEnum.NoTrump,
    shortCode: 'NT',
    aliases: ['NT', 'NOTRUMP', 'NO_TRUMP', 'NO TRUMP'],
    symbol: '',
    color: 'black',
    isStandard: false,
    isPhysical: false,
    isTrump: true,
  },
} as const satisfies Record<CanonicalSuit, SuitMetadata>;

// Standard suits for 52-card decks, derived in standard order: Spades, Hearts, Diamonds, Clubs
export const STANDARD_SUITS: CardSuit[] = (
  Object.values(SUIT_METADATA) as readonly SuitMetadata[]
)
  .filter((entry): entry is SuitMetadata & { canonical: CardSuit } => entry.isStandard)
  .map(entry => entry.canonical as CardSuit);

/**
 * Pre-computed alias lookup map initialized once at module load.
 */
const SUIT_ALIASES = new Map<string, CanonicalSuit>();

for (const meta of Object.values(SUIT_METADATA)) {
  SUIT_ALIASES.set(meta.canonical.toUpperCase(), meta.canonical);
  for (const alias of meta.aliases) {
    SUIT_ALIASES.set(alias.toUpperCase(), meta.canonical);
  }
}

/**
 * Normalizes an arbitrary suit string into its canonical representation.
 * Case-insensitive, trims surrounding whitespace, and preserves unknown strings.
 */
export function normalizeSuit(suit?: string | null): string {
  if (!suit) return '';
  const trimmed = suit.trim();
  if (!trimmed) return '';
  return SUIT_ALIASES.get(trimmed.toUpperCase()) ?? trimmed;
}

/**
 * Retrieves the metadata entry for the given suit or alias, if recognized.
 */
export function getSuitMetadata(suit?: string | null): SuitMetadata | undefined {
  const norm = normalizeSuit(suit);
  return (SUIT_METADATA as Record<string, SuitMetadata | undefined>)[norm];
}

/**
 * Translates a suit to its compact wire/storage code ('S', 'H', 'C', 'D', 'JOKER', 'NT').
 * Falls back to the original input string for unknown suits.
 */
export function toShortSuitCode(suit?: string | null): string {
  if (!suit) return '';
  return getSuitMetadata(suit)?.shortCode ?? suit;
}

/**
 * Returns true if two suit representations refer to the same canonical suit.
 */
export function areSuitsEqual(a?: string | null, b?: string | null): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return normalizeSuit(a) === normalizeSuit(b);
}

/**
 * Core generic helper to compare a suit string against an expected canonical suit.
 */
export function isSuit(
  suit: string | null | undefined,
  expected: CanonicalSuit
): boolean {
  return normalizeSuit(suit) === expected;
}

export const isSpades = (suit?: string | null): boolean => isSuit(suit, CardSuit.Spades);
export const isHearts = (suit?: string | null): boolean => isSuit(suit, CardSuit.Hearts);
export const isClubs = (suit?: string | null): boolean => isSuit(suit, CardSuit.Clubs);
export const isDiamonds = (suit?: string | null): boolean => isSuit(suit, CardSuit.Diamonds);
export const isJoker = (suit?: string | null): boolean => isSuit(suit, CardSuit.Joker);
export const isNoTrump = (suit?: string | null): boolean => isSuit(suit, TrumpSuitEnum.NoTrump);

export function isRedSuit(suit?: string | null): boolean {
  const meta = getSuitMetadata(suit);
  return Boolean(meta?.isStandard && meta.color === 'red');
}

export function isBlackSuit(suit?: string | null): boolean {
  const meta = getSuitMetadata(suit);
  return Boolean(meta?.isStandard && meta.color === 'black');
}

export function getSuitSymbol(suit?: string | null): string {
  return getSuitMetadata(suit)?.symbol ?? '';
}

export function getSuitColor(suit?: string | null): SuitColor {
  return getSuitMetadata(suit)?.color ?? 'black';
}

export function isStandardSuit(suit?: string | null): boolean {
  return getSuitMetadata(suit)?.isStandard ?? false;
}

export function isPhysicalSuit(suit?: string | null): boolean {
  return getSuitMetadata(suit)?.isPhysical ?? false;
}

export function isTrumpSuit(suit?: string | null): boolean {
  return getSuitMetadata(suit)?.isTrump ?? false;
}
